<template>
  <div>
    <b-overlay
      :show="formShow"
      rounded="true"
      no-fade
      style="height:100vh!important"
    >
      <div>
        <b-card class="mt-2">
          <b-row class="">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col" style="font-size: 1.2em;">
                    REVIEW SUMMARY EXPLANATION - Review ID: {{ surveyId }}
                  </th>
                </tr>
              </thead>
            </table>
          </b-row>

          <!-- Breadcrumb Navigation -->
          <b-breadcrumb class="bg-transparent px-0 py-2 mb-3">
            <b-breadcrumb-item :to="{ name: 'review-list' }">
              <feather-icon icon="ListIcon" size="16" class="mr-1" />
              Review List
            </b-breadcrumb-item>
            <b-breadcrumb-item
              :to="{ name: 'review-edit', params: { id: surveyId } }"
            >
              <feather-icon icon="EditIcon" size="16" class="mr-1" />
              Review
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              <feather-icon icon="SettingsIcon" size="16" class="mr-1" />
              Summary Explanation
            </b-breadcrumb-item>
          </b-breadcrumb>

          <div
            class="m-5"
            v-if="
              maxScore > 0 && !formShow && dataLoaded && ranges.length === 5
            "
          >
            <vue-slider
              :min="0"
              :max="maxScore"
              :tooltip="'always'"
              :dot-options="dotOptions"
              v-model="ranges"
              :interval="1"
            />
          </div>

          <b-alert variant="info" show>
            <div class="alert-body">
              <span>
                Please type
                <strong class="bg-danger p-25 text-white">#totalScore#</strong>
                inside the sentence in order to add the calculated total score.
              </span>
            </div>
          </b-alert>

          <b-row
            class="mb-3 ml-2 mr-2"
            v-if="rangeDatas.length > 0 && maxScore > 0"
          >
            <b-col cols="12" class="mt-2">
              <label for="textarea-1">
                {{ rangeDatas[0].range }} - {{ rangeDatas[1].range }} summary
                explanation
              </label>
              <b-form-textarea
                id="textarea-1"
                v-model="rangeDatas[0].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-2">
                {{ rangeDatas[1].range + 1 }} -
                {{ rangeDatas[2].range }} summary explanation
              </label>
              <b-form-textarea
                id="textarea-2"
                v-model="rangeDatas[1].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-3">
                {{ rangeDatas[2].range + 1 }} -
                {{ rangeDatas[3].range }} summary explanation
              </label>
              <b-form-textarea
                id="textarea-3"
                v-model="rangeDatas[2].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-4">
                {{ rangeDatas[3].range + 1 }} - {{ maxScore }} summary
                explanation
              </label>
              <b-form-textarea
                id="textarea-4"
                v-model="rangeDatas[3].title"
                rows="2"
              />
            </b-col>
          </b-row>

          <b-button
            type="submit"
            @click="updateSummaryExplanation"
            variant="warning"
            class="mr-3 float-right"
            :disabled="maxScore === 0"
          >
            Save
          </b-button>

          <b-button
            variant="secondary"
            class="mr-3 float-right"
            @click="goBack"
          >
            Back to Review
          </b-button>
        </b-card>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import {
  BFormGroup,
  BRow,
  BCol,
  BAlert,
  BButton,
  BCard,
  BOverlay,
  BFormTextarea,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import VueSlider from "vue-slider-component";
import axiosIns from "@/libs/axios";

export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BButton,
    BCard,
    BOverlay,
    BFormTextarea,
    ValidationProvider,
    ValidationObserver,
    VueSlider,
  },
  data() {
    return {
      surveyId: 0,
      maxScore: 100,
      rangeDatas: [
        { range: 0, title: "" },
        { range: 25, title: "" },
        { range: 50, title: "" },
        { range: 75, title: "" },
      ],
      ranges: [0, 25, 50, 75, 100],
      formShow: false,
      dataLoaded: false,
      dotOptions: [
        {
          disabled: true, // First point (0) - always disabled
        },
        {
          disabled: false, // Second point - movable
        },
        {
          disabled: false, // Third point - movable
        },
        {
          disabled: false, // Fourth point - movable
        },
        {
          disabled: true, // Last point (maxScore) - always disabled
        },
      ],
    };
  },
  computed: {},
  watch: {
    ranges(val) {
      if (this.rangeDatas.length > 0 && val.length >= 4) {
        // Update range values but preserve existing titles
        for (let i = 0; i < Math.min(4, this.rangeDatas.length); i++) {
          this.rangeDatas[i].range = val[i];
        }
      }
    },
  },
  methods: {
    async loadReviewData() {
      try {
        this.formShow = true;

        // Load summary data from summaries table by surveyId
        const response = await axiosIns.get(`summaryReview/${this.surveyId}`);

        if (
          response.data &&
          response.data.title &&
          Array.isArray(response.data.title)
        ) {
          this.rangeDatas = response.data.title.map((item) => ({
            range: item.range,
            title: item.title || "",
          }));

          this.maxScore = response.data.maxScore || 100;
          this.ranges = response.data.ranges || [0, 25, 50, 75, 100];

          // Ensure we have exactly 4 range data items
          while (this.rangeDatas.length < 4) {
            const index = this.rangeDatas.length;
            this.rangeDatas.push({
              range: this.ranges[index] || 0,
              title: "",
            });
          }

          // Update ranges in rangeDatas to match the ranges array
          for (let i = 0; i < Math.min(4, this.rangeDatas.length); i++) {
            this.rangeDatas[i].range = this.ranges[i];
          }
        }

        this.formShow = false;
        this.dataLoaded = true;
      } catch (error) {
        this.formShow = false;
        this.dataLoaded = true;
        console.error("Error loading review summary data:", error);

        // If 404, it means no summary exists yet for this review
        if (error.response && error.response.status === 404) {
          console.log("No summary found - using default values");
          // Initialize with default values
          this.rangeDatas = [
            { range: 0, title: "" },
            { range: 25, title: "" },
            { range: 50, title: "" },
            { range: 75, title: "" },
          ];
          this.ranges = [0, 25, 50, 75, 100];
          this.maxScore = 100;
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              text: "Failed to load review summary data",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      }
    },

    async updateSummaryExplanation() {
      try {
        this.formShow = true;

        // Ensure ranges array is always properly formatted with 5 elements
        let validRanges = [...this.ranges];

        // Validate and fix ranges array
        if (!Array.isArray(validRanges) || validRanges.length !== 5) {
          // Create default ranges if invalid
          const quarter = Math.floor(this.maxScore / 4);
          validRanges = [0, quarter, quarter * 2, quarter * 3, this.maxScore];
        }

        // Ensure first element is 0 and last is maxScore
        validRanges[0] = 0;
        validRanges[4] = this.maxScore;

        // Ensure ranges are in ascending order
        for (let i = 1; i < 4; i++) {
          if (
            validRanges[i] <= validRanges[i - 1] ||
            validRanges[i] >= validRanges[i + 1]
          ) {
            validRanges[i] = Math.round(
              validRanges[i - 1] +
                (this.maxScore - validRanges[i - 1]) / (5 - i)
            );
          }
        }

        // Prepare explanations in the correct format - ensure all explanations are included
        const explanations = this.rangeDatas.map((item, index) => ({
          range: item.range !== undefined ? item.range : validRanges[index],
          title:
            item.title !== undefined && item.title !== null ? item.title : "",
        }));

        // Ensure we have exactly 4 explanations
        while (explanations.length < 4) {
          const index = explanations.length;
          explanations.push({
            range: validRanges[index] || 0,
            title: "",
          });
        }

        const updateData = {
          title: explanations,
          maxScore: this.maxScore,
          ranges: validRanges,
          explanations: explanations,
        };

        await axiosIns.put(`summaryReview/${this.surveyId}`, updateData);

        this.formShow = false;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Successful",
            text: "✔️ Review summary updated successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
      } catch (error) {
        this.formShow = false;
        console.error("Error updating review summary:", error);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to update review summary",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    goBack() {
      this.$router.push({
        name: "review-edit",
        params: { id: this.surveyId },
      });
    },
  },
  async created() {
    this.surveyId = this.$route.params.id;
    await this.loadReviewData();
  },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-slider.scss";
</style>
